"use client"

import { useState, useMemo } from "react"
import { RotateCcw } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn, numeroEn } from "./formato"

const T = {
  es: {
    datosEmpleado: "Datos del empleado",
    limpiar: "Limpiar",
    brutoAnual: "Salario bruto anual (€)",
    tipoContrato: "Tipo de contrato",
    indefinido: "Indefinido",
    temporal: "Temporal",
    numPagas: "Número de pagas",
    doce: "12 pagas",
    catorce: "14 pagas (con extras)",
    categoria: "Categoría profesional",
    general: "General",
    directivo: "Directivo",
    tecnico: "Técnico",
    notaTitulo: "Nota:",
    nota: " Este cálculo es orientativo. El IRPF real depende de la situación personal (estado civil, hijos, deducciones). Consulta con un asesor fiscal para datos exactos.",
    resultados: "Resultados",
    costeEmpresa: "Coste Empresa (mensual)",
    porAno: "€/año",
    brutoMensual: "Bruto mensual",
    pagas: (n: number) => `${n} pagas`,
    netoMensual: "Neto mensual",
    desglose: "Desglose",
    ssEmpresa: (pct: number) => `SS Empresa (${pct}%)`,
    ssTrabajador: "SS Trabajador (6.35%)",
    irpf: (pct: string) => `IRPF (${pct}% efectivo)`,
    retenciones: "Retenciones totales",
    referencia: "📊 Referencia",
    refSmi: "• SMI 2024: ~15.876€ brutos/año",
    refMedio: "• Salario medio hostelería: ~20.000-25.000€/año",
    refCoste: "• Coste empresa ≈ 130% del bruto",
  },
  en: {
    datosEmpleado: "Employee details",
    limpiar: "Reset",
    brutoAnual: "Gross annual salary (€)",
    tipoContrato: "Contract type",
    indefinido: "Permanent",
    temporal: "Fixed-term",
    numPagas: "Number of payments a year",
    doce: "12 payments",
    catorce: "14 payments (with the two Spanish bonus payments)",
    categoria: "Job category",
    general: "General",
    directivo: "Management",
    tecnico: "Technical",
    notaTitulo: "Note:",
    nota: " This is an estimate. Actual IRPF (Spanish income tax withholding) depends on personal circumstances such as marital status, children and allowances. Ask a tax adviser (asesor fiscal) for exact figures.",
    resultados: "Results",
    costeEmpresa: "Cost to the employer (monthly)",
    porAno: "/year",
    brutoMensual: "Gross monthly pay",
    pagas: (n: number) => `${n} payments`,
    netoMensual: "Net monthly pay",
    desglose: "Breakdown",
    ssEmpresa: (pct: number) => `Spanish Social Security, employer (${pct}%)`,
    ssTrabajador: "Social Security, employee (6.35%)",
    irpf: (pct: string) => `IRPF income tax (${pct}% effective)`,
    retenciones: "Total deductions",
    referencia: "📊 Benchmarks",
    refSmi: "• Spanish minimum wage (SMI) 2024: ~€15,876 gross a year",
    refMedio: "• Average hospitality salary in Spain: ~€20,000–25,000 a year",
    refCoste: "• Cost to the employer ≈ 130% of gross pay",
  },
}

const IRPF_TRAMOS_2024 = [
  { hasta: 12450, tipo: 19 },
  { hasta: 20200, tipo: 24 },
  { hasta: 35200, tipo: 30 },
  { hasta: 60000, tipo: 37 },
  { hasta: 300000, tipo: 45 },
  { hasta: Infinity, tipo: 47 },
]

function calcularIRPF(baseAnual: number): number {
  let irpfTotal = 0
  let baseRestante = baseAnual
  let limiteAnterior = 0

  for (const tramo of IRPF_TRAMOS_2024) {
    if (baseRestante <= 0) break
    const baseTramo = Math.min(baseRestante, tramo.hasta - limiteAnterior)
    irpfTotal += baseTramo * (tramo.tipo / 100)
    baseRestante -= baseTramo
    limiteAnterior = tramo.hasta
  }

  return irpfTotal
}

const defaultState = {
  salarioBrutoAnual: 28000,
  tipoContrato: "indefinido" as "indefinido" | "temporal",
  pagas: 14 as 12 | 14,
  categoria: "general" as string,
}

export default function CosteSalarioCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const eur = (n: number) => (idioma === "en" ? eurosEn(n) : `${n.toFixed(2)}€`)
  const anual = (n: number) => (idioma === "en" ? `${eurosEn(n)}/year` : `${n.toFixed(2)}€/año`)
  const [salarioBrutoAnual, setSalarioBrutoAnual] = useState(defaultState.salarioBrutoAnual)
  const [tipoContrato, setTipoContrato] = useState(defaultState.tipoContrato)
  const [pagas, setPagas] = useState(defaultState.pagas)
  const [categoria, setCategoria] = useState(defaultState.categoria)

  const results = useMemo(() => {
    if (salarioBrutoAnual <= 0) {
      return {
        costeEmpresaAnual: 0,
        costeEmpresaMensual: 0,
        ssEmpresa: 0,
        ssEmpresaPct: 0,
        brutoMensual: 0,
        ssTrabajador: 0,
        ssTrabajadorAnual: 0,
        irpfAnual: 0,
        irpfMensual: 0,
        irpfPct: 0,
        netoAnual: 0,
        netoMensual: 0,
      }
    }

    // SS Empresa: ~30% (contingencias comunes 23.6%, desempleo 5.5%, FOGASA 0.2%, FP 0.6%)
    const ssEmpresaPct = tipoContrato === "temporal" ? 31.4 : 30.0
    const ssEmpresa = salarioBrutoAnual * (ssEmpresaPct / 100)
    const costeEmpresaAnual = salarioBrutoAnual + ssEmpresa
    const costeEmpresaMensual = costeEmpresaAnual / 12

    // SS Trabajador: 6.35%
    const ssTrabajadorPct = 6.35
    const ssTrabajadorAnual = salarioBrutoAnual * (ssTrabajadorPct / 100)
    const ssTrabajador = ssTrabajadorAnual / pagas

    // Base para IRPF
    const baseIRPF = salarioBrutoAnual - ssTrabajadorAnual
    const irpfAnual = calcularIRPF(baseIRPF)
    const irpfMensual = irpfAnual / pagas
    const irpfPct = (irpfAnual / salarioBrutoAnual) * 100

    // Neto
    const netoAnual = salarioBrutoAnual - ssTrabajadorAnual - irpfAnual
    const netoMensual = netoAnual / pagas
    const brutoMensual = salarioBrutoAnual / pagas

    return {
      costeEmpresaAnual,
      costeEmpresaMensual,
      ssEmpresa,
      ssEmpresaPct,
      brutoMensual,
      ssTrabajador,
      ssTrabajadorAnual,
      irpfAnual,
      irpfMensual,
      irpfPct,
      netoAnual,
      netoMensual,
    }
  }, [salarioBrutoAnual, tipoContrato, pagas, categoria])

  const reset = () => {
    setSalarioBrutoAnual(defaultState.salarioBrutoAnual)
    setTipoContrato(defaultState.tipoContrato)
    setPagas(defaultState.pagas)
    setCategoria(defaultState.categoria)
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.datosEmpleado}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.brutoAnual}
            </label>
            <input
              type="number"
              value={salarioBrutoAnual || ""}
              onChange={(e) => setSalarioBrutoAnual(parseFloat(e.target.value) || 0)}
              placeholder="28000"
              min="0"
              step="100"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.tipoContrato}
            </label>
            <select
              value={tipoContrato}
              onChange={(e) => setTipoContrato(e.target.value as typeof tipoContrato)}
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            >
              <option value="indefinido">{t.indefinido}</option>
              <option value="temporal">{t.temporal}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.numPagas}
            </label>
            <select
              value={pagas}
              onChange={(e) => setPagas(parseInt(e.target.value) as 12 | 14)}
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            >
              <option value={12}>{t.doce}</option>
              <option value={14}>{t.catorce}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.categoria}
            </label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            >
              <option value="general">{t.general}</option>
              <option value="directivo">{t.directivo}</option>
              <option value="tecnico">{t.tecnico}</option>
            </select>
          </div>

          {/* Info box */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-800">
              <strong>{t.notaTitulo}</strong>{t.nota}
            </p>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {/* 3 KPI Cards */}
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-6 text-white">
              <p className="text-sm font-medium text-violet-100 mb-1">{t.costeEmpresa}</p>
              <p className="text-4xl font-bold">{eur(results.costeEmpresaMensual)}</p>
              <p className="text-sm text-violet-200 mt-1">
                {anual(results.costeEmpresaAnual)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <p className="text-xs text-zinc-500 mb-1">{t.brutoMensual}</p>
                <p className="text-2xl font-bold text-zinc-900">
                  {eur(results.brutoMensual)}
                </p>
                <p className="text-xs text-zinc-400">{t.pagas(pagas)}</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
                <p className="text-xs text-emerald-100 mb-1">{t.netoMensual}</p>
                <p className="text-2xl font-bold">{eur(results.netoMensual)}</p>
                <p className="text-xs text-emerald-200">{anual(results.netoAnual)}</p>
              </div>
            </div>
          </div>

          {/* Desglose */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-zinc-900">{t.desglose}</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-600">{t.ssEmpresa(results.ssEmpresaPct)}</span>
                <span className="font-medium text-zinc-900">+{anual(results.ssEmpresa)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">{t.ssTrabajador}</span>
                <span className="font-medium text-red-600">-{anual(results.ssTrabajadorAnual)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">{t.irpf(idioma === "en" ? numeroEn(results.irpfPct, 1) : results.irpfPct.toFixed(1))}</span>
                <span className="font-medium text-red-600">-{anual(results.irpfAnual)}</span>
              </div>
              <div className="border-t border-zinc-100 pt-2 flex justify-between">
                <span className="text-zinc-600">{t.retenciones}</span>
                <span className="font-medium text-red-600">
                  -{anual(results.ssTrabajadorAnual + results.irpfAnual)}
                </span>
              </div>
            </div>
          </div>

          {/* Benchmarks */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.referencia}</h3>
            <ul className="text-sm text-lime-700 space-y-1">
              <li>{t.refSmi}</li>
              <li>{t.refMedio}</li>
              <li>{t.refCoste}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
